// Admin Event Assistant — MVP MOCK ONLY.
//
// Mirrors the News/Reviews AI Assistant (see ./aiAssistant.js): same suggestion
// shape, same mock-only contract. Performs NO internet search, NO AI API call,
// and uses NO API keys. Replace the body of fetchEventSuggestions() later with a
// call to a serverless endpoint that holds the keys and verifies admin access.

import { EVENT_STATUS } from "../config/eventOptions";
import { extractEventDraft } from "../utils/eventImport";
import { parseAtomsSuggestions } from "../utils/atomsImport";

export { confidenceLabel } from "./aiAssistant";

const SAMPLE_LABELS = ["club night", "warehouse session", "rooftop set", "live showcase"];
const SAMPLE_MOODS = ["Party", "Chill", "Creative", "Social"];

// Local, no-network suggestion generator. Seeds the first suggestion from the
// admin's pasted text/URL (via the existing local parser), then adds a few
// mock ideas based on the chosen city / genres / moods.
export const fetchEventSuggestions = async ({
  text = "",
  sourceUrl = "",
  city = "",
  genres = [],
  moods = [],
  startDate = "",
  endDate = "",
} = {}) => {
  await new Promise((resolve) => setTimeout(resolve, 400));

  // If the admin pasted valid Atoms JSON, use it directly — no mock filler.
  const atoms = parseAtomsSuggestions(text);
  if (atoms) {
    return atoms.map((s) => ({ ...s, sourceUrl: s.sourceUrl || sourceUrl }));
  }

  const baseGenres = genres.length ? genres : ["Electronic"];
  const baseMoods = moods.length ? moods : ["Party"];
  const where = city || "your city";
  const range = startDate || endDate ? ` (${startDate || "…"} – ${endDate || "…"})` : "";

  const suggestions = [];

  // 1) Structured from the pasted text / URL, if anything was provided.
  if (text.trim() || sourceUrl.trim()) {
    const parsed = extractEventDraft({ text, url: sourceUrl });
    suggestions.push({
      name: parsed.name || `${baseGenres[0]} event in ${where}`,
      city: parsed.city || city,
      venue: parsed.venue || "",
      date: parsed.date || startDate || "",
      genres: parsed.genres.length ? parsed.genres : baseGenres.slice(0, 2),
      moods: parsed.moods.length ? parsed.moods : baseMoods.slice(0, 2),
      description: parsed.description || `Structured from your pasted text for ${where}.`,
      sourceUrl: parsed.sourceUrl || sourceUrl,
      imageUrl: parsed.imageUrl || "",
      confidence: 0.7,
      notes: "Structured from your pasted text — review before approving.",
    });
  }

  // 2) Mock idea suggestions.
  SAMPLE_LABELS.forEach((label, index) => {
    const genre = baseGenres[index % baseGenres.length];
    suggestions.push({
      name: `${genre} ${label} in ${where}`,
      city,
      venue: "",
      date: startDate || "",
      genres: baseGenres.slice(0, 2),
      moods: [SAMPLE_MOODS[index % SAMPLE_MOODS.length]],
      description: `Mock event suggestion for ${where}${range}. Replace with real AI/search results from the backend later.`,
      sourceUrl: sourceUrl || "",
      imageUrl: "",
      confidence: 0.5 + (index % 3) * 0.15,
      notes: "Mock suggestion — backend AI/search not yet connected. Review before approving.",
    });
  });

  return suggestions;
};

// Turn an accepted suggestion into a Firestore event draft (minus createdAt,
// which the caller adds at write time). Always a draft — needs admin approval.
export const buildEventDraftFromSuggestion = (suggestion, { adminUid }) => {
  const data = {
    name: suggestion.name || "",
    city: suggestion.city || "",
    venue: suggestion.venue || "",
    date: suggestion.date || "",
    genres: suggestion.genres || [],
    moods: suggestion.moods || [],
    description: suggestion.description || "",
    sourceUrl: suggestion.sourceUrl || "",
    confidence: typeof suggestion.confidence === "number" ? suggestion.confidence : null,
    notes: suggestion.notes || "",
    visibility: "public",
    status: EVENT_STATUS.DRAFT,
    source: "ai_suggestion",
    suggestedBy: "ai",
    importedBy: adminUid,
    creatorId: adminUid,
  };
  if (suggestion.imageUrl) data.imageUrl = suggestion.imageUrl;
  if (suggestion.country) data.country = suggestion.country;
  if (typeof suggestion.score === "number") data.score = suggestion.score;
  if (suggestion.scoreReason) data.scoreReason = suggestion.scoreReason;
  if (suggestion.priorityRegion) data.priorityRegion = suggestion.priorityRegion;
  return data;
};
